import { useMemo } from "react";
import {
  AssessmentQuestionType,
  AssessmentType,
  ComplianceProgramManagerEffectivenessReportRowFragment,
  GetComplianceProgramForReportQuery,
  Maybe,
  RangeAssessmentQuestionNode,
  TalentIndicatorAssessmentQuestionNode,
  TalentIndicatorType,
} from "types/graphql-schema";

import { getProgramUrl } from "@apps/programs/helpers";
import { getManagerEffectivenessAnswerPercentage } from "@apps/reporting/helpers";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Callout from "@components/callout/callout";
import Table, {
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableContainer,
  TableHeadCell,
  TableHeadRow,
} from "@components/table/table";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { assertNonNull, getUrlUsingTypename } from "@helpers/helpers";

import AssessmentReportViewOptionsSidebar from "./report-filters/assessment-report-view-options-sidebar";
import { AssessmentReportViewOptions } from "./report-filters/types";

const ManagerEffectivenessTable = ({
  questions,
  complianceProgram,
  viewOptions,
  filteredTableRows,
  showViewOptionsSidebar,
  talentIndicatorQuestions,
  onUpdateViewOptions,
  onCloseViewOptionsSidebar,
  onResetViewToBaseline,
  onSaveView,
  onSaveAsNewView,
  onDuplicateView,
  onDeleteView,
}: {
  complianceProgram: NonNullable<
    GetComplianceProgramForReportQuery["complianceProgram"]
  >;
  questions: Pick<
    RangeAssessmentQuestionNode,
    "id" | "title" | "startValue" | "endValue" | "labels" | "questionType"
  >[];
  filteredTableRows: ComplianceProgramManagerEffectivenessReportRowFragment[];
  viewOptions: AssessmentReportViewOptions;
  talentIndicatorQuestions: (Pick<
    TalentIndicatorAssessmentQuestionNode,
    "id" | "startValue" | "endValue" | "labels"
  > & {
    talentIndicator?: Maybe<{
      id: number;
      name: string;
      customName?: Maybe<string>;
      talentIndicatorType: TalentIndicatorType;
    }>;
  })[];
  showViewOptionsSidebar: boolean;
  onUpdateViewOptions: (
    filterData: Partial<AssessmentReportViewOptions>,
  ) => void;
  onCloseViewOptionsSidebar: () => void;
  onResetViewToBaseline: () => void;
  onSaveView: () => void;
  onSaveAsNewView: () => void;
  onDuplicateView: () => void;
  onDeleteView: () => void;
}) => {
  const label = useLabel();
  const filteredQuestions = useMemo(
    () =>
      questions.filter((q) =>
        [
          AssessmentQuestionType.Range,
          AssessmentQuestionType.TalentIndicator,
        ].includes(q.questionType),
      ),
    [questions],
  );

  const rowNodes = useMemo(
    () =>
      complianceProgram?.managerEffectivenessReport
        ? assertNonNull(complianceProgram.managerEffectivenessReport).edges.map(
            (rowEdge) => {
              return assertNonNull(rowEdge?.node);
            },
          )
        : [],
    [complianceProgram],
  );

  if (filteredQuestions.length === 0) {
    return null;
  }

  return (
    <div>
      {showViewOptionsSidebar && (
        <AssessmentReportViewOptionsSidebar
          assessmentType={AssessmentType.Manager}
          isOngoingProgram={false}
          viewOptions={viewOptions}
          talentIndicatorQuestions={talentIndicatorQuestions}
          onDeleteView={onDeleteView}
          onDuplicateView={onDuplicateView}
          onSaveView={onSaveView}
          onSaveAsNewView={onSaveAsNewView}
          onResetViewToBaseline={onResetViewToBaseline}
          onUpdateViewOptions={onUpdateViewOptions}
          onClose={onCloseViewOptionsSidebar}
        />
      )}
      {filteredQuestions.length === 0 && (
        <Callout
          icon="warning"
          theme="warning"
          title="This program doesn't have any Range Questions"
          center
          className="mb-4"
        >
          Because there aren't any range questions used in this program, there
          are no responses to show.
        </Callout>
      )}
      <TableContainer scroll>
        <Table>
          <TableHeadRow>
            <TableHeadCell cellClassName="w-56">Name</TableHeadCell>
            <TableHeadCell>Responses/Reports</TableHeadCell>
            {filteredQuestions.map((question) => {
              return (
                <TableHeadCell key={question.id}>
                  {question.title}
                </TableHeadCell>
              );
            })}
          </TableHeadRow>
          <TableBody>
            {filteredTableRows.length === 0 && (
              <TableBodyRow>
                <TableBodyCell colSpan={filteredQuestions.length + 2}>
                  {rowNodes.length > 0 &&
                  viewOptions.filters.list.length > 0 ? (
                    <Callout
                      icon="info"
                      theme="transparent"
                      title="No employees matching the applied filters"
                      center
                    >
                      There are several employees in this table that are hidden
                      because of the filters you have applied.
                      <Button
                        text="Clear Filters"
                        onClick={() =>
                          onUpdateViewOptions({
                            filters: {
                              ...viewOptions.filters,
                              list: [],
                            },
                          })
                        }
                      />
                    </Callout>
                  ) : (
                    <Callout
                      icon="info"
                      theme="transparent"
                      title="No responses"
                      center
                    >
                      There haven't been any{" "}
                      {label("review", { pluralize: true })} delivered yet for
                      this program.
                      <Button
                        text="View Program Progress"
                        to={getProgramUrl(complianceProgram.id, {
                          subPath: "/progress",
                        })}
                      />
                    </Callout>
                  )}
                </TableBodyCell>
              </TableBodyRow>
            )}
            {filteredTableRows.map((row) => {
              return (
                <TableBodyRow key={row.manager.id}>
                  <TableBodyCell>
                    <div className="flex items-center justify-between min-w-0 w-56">
                      <div className="flex items-center min-w-0 gap-2 truncate">
                        <Avatar className="" size="6" user={row.manager} />
                        <div className="truncate flex-1">
                          {row.manager.name}
                        </div>
                      </div>
                      <Button
                        tooltip={
                          row.assessmentDelivery?.id
                            ? `View ${label("review")} package`
                            : `No ${label("review")} package available`
                        }
                        icon={TopicflowIconMap.externalLink}
                        disabled={!row.assessmentDelivery?.id}
                        to={
                          row.assessmentDelivery?.id
                            ? getUrlUsingTypename(row.assessmentDelivery)
                            : ""
                        }
                        theme={buttonTheme.iconGray}
                      />
                    </div>
                  </TableBodyCell>
                  <TableBodyCell>
                    {row.assessments.totalCount}/
                    {row.manager.directReports.totalCount}
                  </TableBodyCell>
                  {filteredQuestions.map((question) => {
                    const questionData = row.questions.edges.find(
                      (q) => q?.node?.question.id === question.id,
                    )?.node;

                    if (!questionData) {
                      return (
                        <TableBodyCell key={question.id}>N/A</TableBodyCell>
                      );
                    }
                    return (
                      <TableBodyCell key={question.id}>
                        {questionData.avgAnswer
                          ? `${Math.round(
                              getManagerEffectivenessAnswerPercentage(
                                question,
                                questionData.avgAnswer,
                              ),
                            )}%`
                          : "N/A"}
                      </TableBodyCell>
                    );
                  })}
                </TableBodyRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ManagerEffectivenessTable;
